import { inject, Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';
import { catchError, map, Observable, of } from 'rxjs';
import { Role } from 'src/app/core/constants/role.enum';
import { UserStore } from 'src/app/core/stores/user.store';

@Injectable({
  providedIn: 'root',
})
export class NonUserRouteGuard implements CanActivateChild {
  private readonly router = inject(Router);
  private readonly userStore = inject(UserStore);

  private readonly allowedPrefixes = [
    '/homepage',
    '/notifications',
    '/settings',
    '/recognition',
    '/past-rounds',
    '/not-found',
  ];

  private readonly restrictedPrefixes = ['/past-rounds/roundee', '/past-rounds/template'];

  private readonly pastRoundsUrl = '/past-rounds';
  private readonly ownPastRoundsUrl = '/past-rounds/list';

  canActivateChild(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> | boolean | UrlTree {
    const knownRole = this.userStore.userRole() ?? this.userStore.user().role;
    if (knownRole) {
      return this.checkAccessByRole(knownRole, state.url);
    }

    if (!this.userStore.isAuthorized()) {
      return true;
    }

    return this.userStore.loadCurrentUser().pipe(
      map(user => this.checkAccessByRole(user.role, state.url)),
      catchError(() => of(this.router.parseUrl('/homepage')))
    );
  }

  private checkAccessByRole(role: Role, rawUrl: string): boolean | UrlTree {
    if (role !== Role.NON_USER) return true;

    const url = this.normalizeUrl(rawUrl);
    if (url === this.pastRoundsUrl) return this.router.parseUrl(this.ownPastRoundsUrl);

    const isAllowed = this.matchesAny(this.allowedPrefixes, url) && !this.matchesAny(this.restrictedPrefixes, url);

    return isAllowed ? true : this.router.parseUrl('/homepage');
  }

  private matchesAny(prefixes: string[], url: string): boolean {
    return prefixes.some(prefix => url === prefix || url.startsWith(`${prefix}/`));
  }

  private normalizeUrl(url: string): string {
    return url.split('?')[0].split('#')[0];
  }
}
